import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
// Removed Redux auth selectors - now using useAuth() hook
import { useAuth } from '../context/AuthContext';
import { selectUserFacilities } from '../redux/slices/facilitiesSlice';
import { updateUserProfile } from '../services/userManagementService';
import { updateUserDefaultFacility } from '../redux/slices/authSlice';

/**
 * Custom hook for managing facility switching functionality
 * Provides functions to switch default facility and get current facility info
 */
export const useFacilitySwitching = () => {
  const dispatch = useDispatch();
  const { user } = useAuth();
  const userFacilities = useSelector(selectUserFacilities);

  /**
   * Switch the user's default facility
   * @param {string} newFacilityId - The ID of the new default facility
   * @returns {Promise<Object>} - Result of the facility switch operation
   */
  const switchDefaultFacility = useCallback(async (newFacilityId) => {
    if (!user?.uid || !newFacilityId) {
      throw new Error('User ID and new facility ID are required');
    }

    // Check if the facility is accessible to the user
    const hasAccess = userFacilities.some(f => f.id === newFacilityId);
    if (!hasAccess) {
      throw new Error('User does not have access to this facility');
    }

    try {
      // Find the complete facility object
      const newFacility = userFacilities.find(f => f.id === newFacilityId);
      if (!newFacility) {
        throw new Error('Facility not found');
      }

      // Update the user profile in the database
      await updateUserProfile(user.uid, { defaultFacility: newFacilityId });
      
      // Update the auth state immediately with the complete facility object
      dispatch(updateUserDefaultFacility(newFacility));
      
      // Trigger a custom event to notify other components (including AuthContext)
      window.dispatchEvent(new CustomEvent('defaultFacilityChanged', {
        detail: { 
          facilityId: newFacilityId, 
          newFacility: newFacility,  // Pass complete facility object for AuthContext
          previousFacility: user.defaultFacility,
          timestamp: Date.now()
        }
      }));

      return {
        success: true,
        newFacilityId,
        previousFacility: user.defaultFacility,
        message: 'Default facility updated successfully'
      };
    } catch (error) {
      console.error('Error switching default facility:', error);
      throw error;
    }
  }, [user?.uid, user?.defaultFacility, userFacilities, dispatch]);

  /**
   * Get the current default facility object
   */
  const getCurrentDefaultFacility = useCallback(() => {
    if (!user?.defaultFacility) return null;
    
    // defaultFacility is now always the complete facility object
    return user.defaultFacility;
  }, [user?.defaultFacility]);

  /**
   * Check if a facility is the current default
   */
  const isDefaultFacility = useCallback((facilityId) => {
    if (!user?.defaultFacility) return false;
    
    // defaultFacility is now always an object, compare IDs
    return user.defaultFacility.id === facilityId;
  }, [user?.defaultFacility]);

  /**
   * Get all accessible facilities for the user
   */
  const getAccessibleFacilities = useCallback(() => {
    return userFacilities || [];
  }, [userFacilities]);

  return {
    switchDefaultFacility,
    getCurrentDefaultFacility,
    isDefaultFacility,
    getAccessibleFacilities,
    currentDefaultFacilityId: user?.defaultFacility?.id,
    hasMultipleFacilities: userFacilities && userFacilities.length > 1
  };
};
